import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useLocation } from 'react-router-dom';
import type { User } from 'oidc-client-ts';
import { LOCATION } from '@/app/consts/location.ts';

export function AuthWatcher() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handlingAuthRef = useRef(false);

  useEffect(() => {
    if (!auth?.events) return;

    if (location.pathname === LOCATION.LOGIN) {
      console.debug('[OIDC] AuthWatcher disabled on /login');
      return;
    }

    const startHandling = (): boolean => {
      if (handlingAuthRef.current) return false;
      handlingAuthRef.current = true;
      return true;
    };

    const stopHandling = (): void => {
      handlingAuthRef.current = false;
    };

    const redirectToLogin = async (reason: string): Promise<void> => {
      console.warn(`[OIDC] Redirect to login: ${reason}`);
      try {
        await auth.signinRedirect({ prompt: 'login' });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('[OIDC] signinRedirect failed:', message);
        navigate(LOCATION.LOGIN, { replace: true });
      } finally {
        stopHandling();
      }
    };

    const canRenew = (): boolean => {
      const user = auth.user as User | null;
      if (!user) return false;
      const now = Date.now() / 1000;
      if (!user.refresh_token) return false;
      if (!user.expires_at) return false;
      return user.expires_at >= now - 30;
    };

    const trySilentRenew = async (context: string): Promise<boolean> => {
      console.log(`[OIDC] ${context}: attempting silent renew`);

      if (!canRenew()) {
        console.warn(
          '[OIDC] Skipping silent renew – no valid user/refresh token'
        );
        stopHandling();
        return false;
      }

      try {
        await auth.signinSilent();
        console.info(`[OIDC] ${context}: silent renew OK`);
        stopHandling();
        return true;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        console.warn(`[OIDC] ${context}: silent renew failed -> ${message}`);

        const tokenInactive =
          message.includes('Token is not active') ||
          message.includes('invalid_grant') ||
          message.includes('login_required');

        if (tokenInactive) {
          console.warn(
            '[OIDC] Refresh token invalid or session expired -> forcing re-login'
          );
          await redirectToLogin('Invalid or inactive session');
          return false;
        }

        stopHandling();
        return false;
      }
    };

    const handleUserSignedOut = async (): Promise<void> => {
      if (!startHandling()) return;
      await redirectToLogin('User signed out');
    };

    const handleAccessTokenExpiring = async (): Promise<void> => {
      if (!startHandling()) return;
      const renewed = await trySilentRenew('Token expiring');
      if (!renewed) {
        await redirectToLogin('Could not renew on expiring');
      }
    };

    const handleAccessTokenExpired = async (): Promise<void> => {
      if (!startHandling()) return;
      const renewed = await trySilentRenew('Token expired');
      if (!renewed) {
        await redirectToLogin('Could not renew on expired');
      }
    };

    auth.events.addUserSignedOut(handleUserSignedOut);
    auth.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    auth.events.addAccessTokenExpired(handleAccessTokenExpired);

    const interval = setInterval(async () => {
      if (handlingAuthRef.current) return;
      if (location.pathname === LOCATION.LOGIN) return;

      if (!auth.user) {
        if (!startHandling()) return;
        await redirectToLogin('No active user session');
        return;
      }

      if (!canRenew()) return;

      const expiresAt = auth.user?.expires_at ? auth.user.expires_at * 1000 : 0;
      const msLeft = expiresAt ? expiresAt - Date.now() : 0;

      if (msLeft > 0 && msLeft < 60_000) {
        if (!startHandling()) return;
        const renewed = await trySilentRenew('Periodic check');
        if (!renewed) {
          await redirectToLogin('Periodic renew failed');
        }
      }
    }, 120_000);

    return () => {
      clearInterval(interval);
      auth.events.removeUserSignedOut(handleUserSignedOut);
      auth.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      auth.events.removeAccessTokenExpired(handleAccessTokenExpired);
    };
  }, [auth, navigate, location.pathname]);

  return null;
}
